/**
 * LOGIN-LINK — where a link-borne session survives a reload: `sessionStorage`, one tab, one origin.
 *
 * ⚠ THE ONE EXCEPTION TO "TOKENS LIVE IN MEMORY ONLY" (sessionStore), and it is deliberately narrow.
 * A session opened from an admin-minted sign-in link has NO refresh token — Identity issues an access
 * token only, ending at a deadline the admin chose (≤ 2 h) — so the usual reload path (restore from
 * the httpOnly refresh cookie) has nothing to restore from, and a plain F5 would sign the user out
 * for good, because the link is single-use. Product chose "survive reloads"; this is the smallest
 * store that does it:
 *   - sessionStorage, not localStorage: gone when the tab closes, never shared with another tab.
 *   - the access token only, never a refresh token (there is none).
 *   - its own deadline beside it, so an expired entry is dropped on read rather than presented.
 *
 * Kept free of imports so `sessionStore.clearSession()` can clear it without an import cycle — every
 * sign-out path (logout, a failed refresh, a failed restore) goes through clearSession.
 */

const STORAGE_KEY = 'zc_link_session';

export interface LinkSession {
  accessToken: string;
  /** ISO-8601 UTC — the link's deadline, which is also the token's exp. */
  expiresAt: string;
}

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null; // storage blocked (privacy mode, sandboxed iframe) — a link session then lasts until reload
  }
}

/** The live link session in this tab, or null. An expired or malformed entry is removed. */
export function readLinkSession(nowMs: number = Date.now()): LinkSession | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<LinkSession>;
    const deadline = parsed.expiresAt ? Date.parse(parsed.expiresAt) : NaN;
    if (typeof parsed.accessToken === 'string' && parsed.accessToken && Number.isFinite(deadline) && deadline > nowMs) {
      return { accessToken: parsed.accessToken, expiresAt: parsed.expiresAt as string };
    }
  } catch {
    /* fall through to removal */
  }
  clearLinkSession();
  return null;
}

export function storeLinkSession(session: LinkSession): void {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* quota or blocked storage: the session still works until the next reload */
  }
}

export function clearLinkSession(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
